import type {
  DrawingElement,
  DrawingElementDescription,
  DrawingElementDescriptionData,
} from 'features/Procedure/state/procedure.type'
import { DrawMode } from 'features/Procedure/state/procedure.type'

export function isUnitsNoteDrawMode(drawMode: DrawMode): boolean {
  const unitsNoteDrawModes = [
    DrawMode.X,
    DrawMode.PIN,
    DrawMode.FREE_LINE,
    DrawMode.STRAIGHT_LINE,
    DrawMode.MEASURE,
  ]
  return unitsNoteDrawModes.includes(drawMode)
}

export function normalizeDrawingDescription(
  desc: DrawingElementDescription | undefined,
): DrawingElementDescriptionData {
  if (desc == null) {
    return { text: '', isUnitsChecked: false }
  }
  if (typeof desc === 'string') {
    try {
      const parsed = JSON.parse(desc) as unknown
      if (parsed && typeof parsed === 'object' && 'text' in (parsed as object)) {
        const p = parsed as { text?: unknown; isUnitsChecked?: unknown }
        return {
          text: String(p.text ?? ''),
          isUnitsChecked: Boolean(p.isUnitsChecked),
        }
      }
    } catch {
      /* plain string */
    }
    return { text: desc, isUnitsChecked: false }
  }
  return {
    text: desc.text ?? '',
    isUnitsChecked: Boolean(desc.isUnitsChecked),
  }
}

export function normalizeDrawingDescriptionFromApi(
  raw: unknown,
): DrawingElementDescription | undefined {
  if (raw == null || raw === '') return undefined
  if (typeof raw === 'object' && raw !== null && 'text' in raw) {
    const o = raw as { text?: unknown; isUnitsChecked?: unknown }
    return {
      text: String(o.text ?? ''),
      isUnitsChecked: Boolean(o.isUnitsChecked),
    }
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (parsed && typeof parsed === 'object' && 'text' in (parsed as object)) {
        const o = parsed as { text?: unknown; isUnitsChecked?: unknown }
        return {
          text: String(o.text ?? ''),
          isUnitsChecked: Boolean(o.isUnitsChecked),
        }
      }
    } catch {
      return raw
    }
    return raw
  }
  return undefined
}

/** Label on canvas and in the Notes table (text + optional " units"). */
export function formatDrawingNoteDisplay(desc: DrawingElementDescription | undefined): string {
  const { text, isUnitsChecked } = normalizeDrawingDescription(desc)
  if (!text.trim()) {
    return ''
  }
  return isUnitsChecked ? `${text} units` : text
}

export function drawingElementHasNoteContent(element: DrawingElement): boolean {
  return formatDrawingNoteDisplay(element.description).length > 0
}

export function serializeDrawingDescriptionForApi(
  desc: DrawingElementDescription | undefined,
  drawMode: DrawMode,
): string | DrawingElementDescriptionData | undefined {
  const n = normalizeDrawingDescription(desc)
  if (isUnitsNoteDrawMode(drawMode)) {
    if (!n.text.trim() && !n.isUnitsChecked) {
      return undefined
    }
    return { text: n.text.trim(), isUnitsChecked: n.isUnitsChecked }
  }
  if (!n.text.trim()) {
    return undefined
  }
  return n.text.trim()
}

/** Sums numeric text from drawings with Units note checked (for procedure_units). */
export function sumUnitsMarksForProcedure(
  elements: DrawingElement[],
  procedureId: number,
): { sum: number; hasUnitMarks: boolean } {
  let sum = 0
  let hasUnitMarks = false
  for (const el of elements) {
    if (el.procedureId !== procedureId || !isUnitsNoteDrawMode(el.drawMode)) continue
    const n = normalizeDrawingDescription(el.description)
    if (!n.isUnitsChecked) continue
    hasUnitMarks = true
    const normalizedNum = n.text.replace(',', '.').trim()
    const value = parseFloat(normalizedNum)
    if (!Number.isNaN(value)) {
      sum += value
    }
  }
  return { sum, hasUnitMarks }
}
